import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assessmentService } from "../services/assessmentService";
import type { Assessment } from "../types/assessment";

export function AssessmentListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const data = await assessmentService.list();
      setAssessments(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleTogglePublish = async (id: string) => {
    try {
      const updated = await assessmentService.togglePublish(id);
      setAssessments((prev) =>
        prev.map((a) =>
          a.assessment_id === id ? { ...a, is_published: updated.is_published } : a
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(t("assessment.delete_confirm", { title }))) return;
    try {
      await assessmentService.delete(id);
      setAssessments((prev) => prev.filter((a) => a.assessment_id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400 text-sm">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("assessment.title")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("assessment.subtitle")}</p>
        </div>
        <button
          onClick={() => navigate("/assessments/new")}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          + {t("assessment.create")}
        </button>
      </div>

      {assessments.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-gray-500 text-sm">{t("assessment.no_assessments")}</p>
          <button
            onClick={() => navigate("/assessments/new")}
            className="mt-4 text-indigo-600 text-sm font-medium hover:underline"
          >
            {t("assessment.create_first")}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_title")}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_questions")}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_status")}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_attempts")}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_pass_rate")}
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_avg_score")}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t("assessment.col_actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {assessments.map((a) => (
                <tr key={a.assessment_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{a.title}</div>
                    {a.description && (
                      <div className="text-xs text-gray-400 truncate max-w-xs">{a.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">
                    {a.question_count ?? a.questions?.length ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.is_published
                          ? "bg-green-50 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {a.is_published ? t("assessment.published") : t("assessment.draft")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">{a.total_attempts ?? 0}</td>
                  <td className="px-4 py-3 text-center text-gray-600">
                    {a.pass_rate != null ? `${a.pass_rate}%` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-600">
                    {a.avg_score != null ? `${a.avg_score}%` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/assessments/${a.assessment_id}/results`)}
                        className="text-xs text-indigo-600 hover:underline font-medium"
                      >
                        {t("assessment.results")}
                      </button>
                      <button
                        onClick={() => navigate(`/assessments/${a.assessment_id}/edit`)}
                        className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                      >
                        {t("common.edit")}
                      </button>
                      <button
                        onClick={() => handleTogglePublish(a.assessment_id)}
                        className={`text-xs font-medium ${
                          a.is_published
                            ? "text-amber-600 hover:text-amber-800"
                            : "text-green-600 hover:text-green-800"
                        }`}
                      >
                        {a.is_published ? t("assessment.unpublish") : t("assessment.publish")}
                      </button>
                      <button
                        onClick={() => handleDelete(a.assessment_id, a.title)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        {t("common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
